/**
 * UTM capture, persistence, and visit pinging.
 *
 * The flow:
 *  - On every route change, `captureUtm()` reads utm_* params off the URL.
 *    If any are present, they replace the session's stored UTM context
 *    (last-touch attribution within a session).
 *  - `pingVisit(path)` fires a fire-and-forget POST /api/track/visit with
 *    the current UTM context plus the visitor id, deduped per session so
 *    a single visitor moving across many pages still counts once per page.
 *  - `getStoredUtm()` returns the current UTM context for axios to splat
 *    onto every conversion POST.
 *
 * Visitor id is persisted in localStorage so returning visitors get the
 * same id, which gives us "unique visitor" counts in the report. The id
 * is opaque (a random string) — no PII.
 */

const STORAGE_KEY = "haevn_utm";
const VISITOR_KEY = "haevn_visitor_id";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
// Paid-platform click IDs. Captured alongside UTMs so survey events can
// be tied back to the exact ad click (offline conversion uploads to
// Google Ads / Meta / TikTok).
const CLICK_ID_KEYS = ["gclid", "fbclid", "ttclid", "msclkid"];

const safe = (fn, fallback = null) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export function captureUtm() {
  if (typeof window === "undefined") return null;
  return safe(() => {
    const params = new URLSearchParams(window.location.search);
    const incoming = {};
    let hasAny = false;
    for (const k of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
      const v = params.get(k);
      if (v) {
        incoming[k] = v.slice(0, 200);
        hasAny = true;
      }
    }
    if (hasAny) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(incoming));
      return incoming;
    }
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  });
}

export function getStoredUtm() {
  if (typeof window === "undefined") return {};
  return (
    safe(() => {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    }, {}) || {}
  );
}

export function getVisitorId() {
  if (typeof window === "undefined") return "anon";
  return (
    safe(() => {
      let id = localStorage.getItem(VISITOR_KEY);
      if (!id) {
        id =
          "v_" +
          Math.random().toString(36).slice(2, 10) +
          Date.now().toString(36);
        localStorage.setItem(VISITOR_KEY, id);
      }
      return id;
    }, "anon") || "anon"
  );
}

export function pingVisit(path) {
  if (typeof window === "undefined") return;
  safe(() => {
    const utm = getStoredUtm();
    const visitor_id = getVisitorId();
    const dedupeKey = `haevn_visit_${visitor_id}_${path}_${
      utm.utm_source || ""
    }_${utm.utm_campaign || ""}`;
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, "1");
    const body = {
      visitor_id,
      path,
      referrer: document.referrer || null,
      user_agent: (navigator.userAgent || "").slice(0, 500),
      ...utm,
    };
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  });
}
